import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2, Upload, ImageOff, X } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { GEO_CONFIGS, parentFieldName } from "../../lib/masterConfig";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const MAX_LOGO_BYTES = 1024 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function LogoField({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Image must be under 1 MB.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange(dataUrl);
    } catch {
      toast.error("Could not read the selected file.");
    } finally {
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-4">
      <div className="w-16 h-16 rounded-md border border-black/10 bg-[#FAFAFA] flex items-center justify-center overflow-hidden shrink-0">
        {value ? (
          <img src={value} alt="Logo" className="w-full h-full object-contain" />
        ) : (
          <ImageOff className="h-5 w-5 text-[#A3A3A3]" />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={testid}
        />
        <Button
          type="button"
          variant="outline"
          className="border-black/10"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-1.5" />
          {value ? "Replace" : "Upload"}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onChange("")}
            className="text-[#525252] hover:text-[#F50E02]"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function buildMap(items) {
  const m = {};
  for (const it of items) m[it.id] = it;
  return m;
}

export default function GeoCrudPage({ config }) {
  const { label, labelPlural, apiPath, parents, extraFields = [], namePlaceholder, columns = 1 } = config;
  const { can } = useAuth();
  const canAdd = can("master_data", "add");
  const canEdit = can("master_data", "edit");
  const canDelete = can("master_data", "delete");

  const [items, setItems] = useState([]);
  const [parentData, setParentData] = useState({}); // key -> list
  const [extraOptions, setExtraOptions] = useState({}); // extra-field-key -> list
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited or null for new
  const [form, setForm] = useState({ name: "" }); // includes <parent>_id keys
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Build maps for parent entities (id -> object) — for rendering table + cascades
  const parentMaps = useMemo(() => {
    const maps = {};
    for (const k of Object.keys(parentData)) {
      maps[k] = buildMap(parentData[k]);
    }
    return maps;
  }, [parentData]);

  const extraOptionMaps = useMemo(() => {
    const maps = {};
    for (const k of Object.keys(extraOptions)) {
      maps[k] = buildMap(extraOptions[k]);
    }
    return maps;
  }, [extraOptions]);

  // Fetch all data needed
  const loadAll = async () => {
    setLoading(true);
    try {
      const selectExtras = extraFields.filter((f) => f.type === "select");
      const [ownRes, ...rest] = await Promise.all([
        api.get(`/master/${apiPath}`),
        ...parents.map((p) => api.get(`/master/${GEO_CONFIGS[p].apiPath}`)),
        ...selectExtras.map((f) => api.get(`/master/${f.optionsApiPath}`)),
      ]);
      setItems(ownRes.data || []);
      const pData = {};
      parents.forEach((p, idx) => {
        pData[p] = rest[idx].data || [];
      });
      setParentData(pData);
      const eData = {};
      selectExtras.forEach((f, idx) => {
        eData[f.key] = rest[parents.length + idx].data || [];
      });
      setExtraOptions(eData);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath]);

  // Filter the rows (search by name)
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.name || "").toLowerCase().includes(q));
  }, [items, search]);

  // ------- Form helpers -------
  const openCreate = () => {
    const blank = { name: "" };
    parents.forEach((p) => {
      blank[parentFieldName(p)] = "";
    });
    extraFields.forEach((f) => {
      blank[f.key] = "";
    });
    setForm(blank);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const values = { name: row.name || "" };
    parents.forEach((p) => {
      values[parentFieldName(p)] = row[parentFieldName(p)] || "";
    });
    extraFields.forEach((f) => {
      values[f.key] = row[f.key] || "";
    });
    setForm(values);
    setEditing(row);
    setDialogOpen(true);
  };

  const updateParent = (parentKey, value) => {
    setForm((prev) => {
      const next = { ...prev, [parentFieldName(parentKey)]: value };
      // Reset all descendant parent selects (in this form's order)
      const idx = parents.indexOf(parentKey);
      for (let i = idx + 1; i < parents.length; i += 1) {
        next[parentFieldName(parents[i])] = "";
      }
      return next;
    });
  };

  // For a given parent select in our form, options = parent items whose ancestor ids
  // match the currently selected values — but only require ancestors that are ALSO
  // present in THIS form's parent list (so entities can skip levels).
  const optionsForParent = (parentKey) => {
    const parentConfig = GEO_CONFIGS[parentKey];
    const list = parentData[parentKey] || [];
    return list.filter((item) => {
      for (const ancestorKey of parentConfig.parents) {
        if (!parents.includes(ancestorKey)) continue; // skipped level
        const selected = form[parentFieldName(ancestorKey)];
        if (!selected) return false;
        if (item[parentFieldName(ancestorKey)] !== selected) return false;
      }
      return true;
    });
  };

  const parentSelectDisabled = (parentKey) => {
    const parentConfig = GEO_CONFIGS[parentKey];
    for (const ancestorKey of parentConfig.parents) {
      if (!parents.includes(ancestorKey)) continue;
      if (!form[parentFieldName(ancestorKey)]) return true;
    }
    return false;
  };

  const formValid = () => {
    if (!form.name?.trim()) return false;
    for (const p of parents) {
      if (!form[parentFieldName(p)]) return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!formValid()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/master/${apiPath}/${editing.id}`, form);
        setItems((prev) => prev.map((it) => (it.id === editing.id ? data : it)));
        toast.success(`${label} updated`);
      } else {
        const { data } = await api.post(`/master/${apiPath}`, form);
        setItems((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`${label} created`);
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/master/${apiPath}/${deleteTarget.id}`);
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      toast.success(`${label} deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const parentLabel = (parentKey, row) => {
    const id = row[parentFieldName(parentKey)];
    if (!id) return "—";
    const obj = parentMaps[parentKey]?.[id];
    return obj?.name || "—";
  };

  return (
    <div data-testid={`master-${apiPath}-page`}>
      {/* Page header */}
      <header className="bg-white border-b border-black/5 px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            {labelPlural}
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            Manage {labelPlural.toLowerCase()} used across the panel.
          </p>
        </div>
        {canAdd ? (
          <Button
            onClick={openCreate}
            className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10 rounded-md"
            data-testid="add-button"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add {label}
          </Button>
        ) : null}
      </header>

      <div className="p-8">
        {/* Toolbar */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
            <Input
              placeholder={`Search ${labelPlural.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-white border-black/10"
              data-testid="search-input"
            />
          </div>
          <span className="text-xs text-[#525252]" data-testid="count-badge">
            {filteredRows.length} {filteredRows.length === 1 ? "record" : "records"}
          </span>
        </div>

        {/* Table */}
        <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="data-table">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    #
                  </th>
                  {parents.map((p) => (
                    <th
                      key={p}
                      className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]"
                    >
                      {GEO_CONFIGS[p].label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">
                    {label} Name
                  </th>
                  {extraFields.map((f) => (
                    <th
                      key={f.key}
                      className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]"
                    >
                      {f.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252] text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={parents.length + extraFields.length + 3} className="px-4 py-10 text-center text-[#525252]">
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={parents.length + extraFields.length + 3}
                      className="px-4 py-10 text-center text-[#525252]"
                      data-testid="empty-row"
                    >
                      {search
                        ? `No ${labelPlural.toLowerCase()} match "${search}".`
                        : `No ${labelPlural.toLowerCase()} yet. Click "Add ${label}" to create the first one.`}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => (
                    <tr
                      key={row.id}
                      className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60"
                      data-testid={`row-${row.id}`}
                    >
                      <td className="px-4 py-3 text-[#525252]">{idx + 1}</td>
                      {parents.map((p) => (
                        <td key={p} className="px-4 py-3 text-[#0A0A0A]">
                          {parentLabel(p, row)}
                        </td>
                      ))}
                      <td className="px-4 py-3 font-medium text-[#0A0A0A]">{row.name}</td>
                      {extraFields.map((f) => (
                        <td key={f.key} className="px-4 py-3 text-[#0A0A0A] max-w-xs">
                          {f.type === "logo" ? (
                            row[f.key] ? (
                              <img
                                src={row[f.key]}
                                alt={row.name}
                                className="h-10 w-10 object-contain rounded border border-black/5 bg-[#FAFAFA]"
                              />
                            ) : (
                              <span className="text-[#A3A3A3]">—</span>
                            )
                          ) : f.type === "textarea" ? (
                            row[f.key] ? (
                              <span className="block truncate" title={row[f.key]}>
                                {row[f.key]}
                              </span>
                            ) : (
                              <span className="text-[#A3A3A3]">—</span>
                            )
                          ) : f.type === "select" ? (
                            row[f.key] && extraOptionMaps[f.key]?.[row[f.key]] ? (
                              extraOptionMaps[f.key][row[f.key]].name
                            ) : (
                              <span className="text-[#A3A3A3]">—</span>
                            )
                          ) : row[f.key] ? (
                            row[f.key]
                          ) : (
                            <span className="text-[#A3A3A3]">—</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="Edit"
                              data-testid={`edit-${row.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(row)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="Delete"
                              data-testid={`delete-${row.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                          {!canEdit && !canDelete ? (
                            <span className="text-[#A3A3A3] text-xs">—</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={columns === 2 ? "sm:max-w-2xl" : "sm:max-w-md"}>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${label}` : `Add ${label}`}
            </DialogTitle>
            <DialogDescription>
              {parents.length
                ? `Select the parent ${parents.map((p) => GEO_CONFIGS[p].label.toLowerCase()).join(", ")} and enter the ${label.toLowerCase()} details.`
                : `Enter the ${label.toLowerCase()} name.`}
            </DialogDescription>
          </DialogHeader>

          <div
            className={
              columns === 2
                ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
                : "space-y-4"
            }
          >
            {parents.map((p) => {
              const fieldName = parentFieldName(p);
              const disabled = parentSelectDisabled(p);
              const opts = optionsForParent(p);
              return (
                <div key={p}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    Select {GEO_CONFIGS[p].label}
                  </Label>
                  <Select
                    value={form[fieldName] || ""}
                    onValueChange={(v) => updateParent(p, v)}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      className="h-10 bg-white border-black/10"
                      data-testid={`select-${p}`}
                    >
                      <SelectValue
                        placeholder={
                          disabled
                            ? `Select ${GEO_CONFIGS[GEO_CONFIGS[p].parents.slice(-1)[0]]?.label || "parent"} first`
                            : `Choose a ${GEO_CONFIGS[p].label.toLowerCase()}`
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {opts.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-[#525252]">
                          No {GEO_CONFIGS[p].labelPlural.toLowerCase()} available
                        </div>
                      ) : (
                        opts.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                {label} Name
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={namePlaceholder || `Enter ${label.toLowerCase()} name`}
                className="h-10 bg-white border-black/10"
                data-testid="name-input"
                autoFocus
              />
            </div>

            {extraFields.map((f) =>
              f.type === "logo" ? (
                <div key={f.key} className={columns === 2 ? "sm:col-span-2" : ""}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    {f.label}
                  </Label>
                  <LogoField
                    value={form[f.key] || ""}
                    onChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}
                    testid={`${f.key}-input`}
                  />
                </div>
              ) : f.type === "textarea" ? (
                <div key={f.key} className={columns === 2 ? "sm:col-span-2" : ""}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    {f.label}
                  </Label>
                  <Textarea
                    value={form[f.key] || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                    }
                    placeholder={f.placeholder || `Enter ${f.label.toLowerCase()}`}
                    rows={3}
                    className="bg-white border-black/10"
                    data-testid={`${f.key}-input`}
                  />
                </div>
              ) : f.type === "select" ? (
                <div key={f.key}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    {f.label}
                  </Label>
                  <Select
                    value={form[f.key] || ""}
                    onValueChange={(v) =>
                      setForm((prev) => ({ ...prev, [f.key]: v }))
                    }
                  >
                    <SelectTrigger
                      className="h-10 bg-white border-black/10"
                      data-testid={`${f.key}-select`}
                    >
                      <SelectValue
                        placeholder={f.placeholder || `Select ${f.label.toLowerCase()}`}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(extraOptions[f.key] || []).length === 0 ? (
                        <div className="px-3 py-2 text-xs text-[#525252]">
                          No {f.label.toLowerCase()} available. Add some in Master Data first.
                        </div>
                      ) : (
                        (extraOptions[f.key] || []).map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div key={f.key}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    {f.label}
                  </Label>
                  <Input
                    value={form[f.key] || ""}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                    }
                    placeholder={f.placeholder || `Enter ${f.label.toLowerCase()}`}
                    className="h-10 bg-white border-black/10"
                    data-testid={`${f.key}-input`}
                  />
                </div>
              )
            )}
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-black/10"
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formValid()}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="save-button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <b>{deleteTarget?.name}</b>. This cannot be undone.
              Records that depend on this {label.toLowerCase()} will block the delete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="delete-confirm"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
